'use client';

import type { CSSProperties } from 'react';
import type { PPTTextElement } from '@openmaic/dsl';
import { useElementShadow } from '../hooks/useElementShadow';
import { ElementOutline } from '../ElementOutline';

export interface BaseTextElementProps {
  elementInfo: PPTTextElement;
  target?: string;
}

/**
 * Base text element component (read-only)
 *
 * 【2026-07-13 vendor P0 修复】同步 OpenMAIC 官方 main (50d3abc) 抽出到
 * @openmaic/renderer 的新版 BaseTextElement。修老板报的"第五张 PPT 字上有字"
 * 关键 3 个根因：
 *  1. vAlign 处理：rotate-wrapper 改用 flex column + justifyContent，
 *     PowerPoint 文本框 vAlign=middle/bottom 文字正确居中/底对齐（老版完全没处理，
 *     文字永远靠左上，导致 vAlign=middle 的文字跟下方元素"字叠字"）。
 *  2. element-content 宽度溢出：老版 `width: ${elementInfo.width}px` + Tailwind
 *     `p-[10px]` (默认 content-box) → 实际宽度 = elementInfo.width + 20px，
 *     文字超出绝对定位容器宽度，溢出到右边跟其他元素字重叠。新版 `width: '100%'`
 *     严格填满外层容器，不溢出。
 *  3. backgroundColor 移到最外层 absolute div：老版在内层 element-content
 *     （宽 = elementInfo.width），新版在最外层（宽 = elementInfo.width = 容器宽），
 *     背景色覆盖范围跟文字一致。
 *
 * 跟官方新版 vendor 的 3 处 import 路径差异（保 prod 现有 import 不动）：
 *  - `'../shared/useElementShadow'` → `'../hooks/useElementShadow'`（prod 老版
 *    hooks 跟官方新版 shared 逻辑一致，已 byte-diff 验证）
 *  - `'../shared/ElementOutline'` → `'../ElementOutline'`（同上）
 *
 * 配套修复：ScreenCanvas.tsx 顶部加了 `<style dangerouslySetInnerHTML={{ __html:
 * SLIDE_RENDERER_STYLES }} />`（copy 自 packages/@openmaic/renderer/src/styles.ts），
 * 否则 `slide-renderer-prose` className 失效，`<p>` 间距仍走浏览器默认 1em。
 */
export function BaseTextElement({ elementInfo, target }: BaseTextElementProps) {
  const { shadowStyle } = useElementShadow(elementInfo.shadow);

  const vAlign = elementInfo.vAlign ?? 'top';
  const justifyContent =
    vAlign === 'middle' ? 'center' : vAlign === 'bottom' ? 'flex-end' : 'flex-start';

  return (
    <div
      className="base-element-text"
      style={{
        position: 'absolute',
        top: `${elementInfo.top}px`,
        left: `${elementInfo.left}px`,
        width: `${elementInfo.width}px`,
        height: `${elementInfo.height}px`,
        // PowerPoint fills the entire shape rectangle with its solid/gradient
        // fill, regardless of how much vertical room the text actually
        // occupies. The inner .element-content div has height: auto so flex
        // alignment can park the text at top/middle/bottom — but if the
        // background lived there, a tall shape with short text (e.g. the 22
        // empty paragraphs that author full-bleed black slide backgrounds in
        // some decks) would only fill the content height and the slide's own
        // background would bleed through below.
        backgroundColor: elementInfo.fill,
        opacity: elementInfo.opacity,
      }}
    >
      <div
        className="rotate-wrapper"
        style={{
          width: '100%',
          height: '100%',
          transform: `rotate(${elementInfo.rotate}deg)`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent,
        }}
      >
        <div
          className="element-content slide-renderer-prose"
          style={{
            position: 'relative',
            width: elementInfo.vertical ? 'auto' : '100%',
            height: elementInfo.vertical ? '100%' : 'auto',
            textShadow: shadowStyle,
            lineHeight: elementInfo.lineHeight,
            letterSpacing:
              elementInfo.wordSpace !== undefined ? `${elementInfo.wordSpace}px` : undefined,
            color: elementInfo.defaultColor,
            fontFamily: elementInfo.defaultFontName,
            writingMode: elementInfo.vertical ? 'vertical-rl' : 'horizontal-tb',
            ...(elementInfo.paragraphSpace !== undefined
              ? ({ '--paragraphSpace': `${elementInfo.paragraphSpace}px` } as CSSProperties)
              : null),
          }}
        >
          <ElementOutline
            width={elementInfo.width}
            height={elementInfo.height}
            outline={elementInfo.outline}
          />
          <div
            className="text ProseMirror-static"
            style={{
              position: 'relative',
              pointerEvents: target === 'thumbnail' ? 'none' : undefined,
            }}
            dangerouslySetInnerHTML={{ __html: elementInfo.content }}
          />
        </div>
      </div>
    </div>
  );
}